// ===================================
// DOCUMENTS PAGE - src/pages/Documents.tsx
// ===================================
import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";
import Header from "../components/Header";
import LoadingSpinner from "../components/LoadingSpinner";
import {
  FileText,
  Upload,
  Download,
  Trash2,
  Eye,
  RefreshCw,
  Calendar,
  FileType,
  HardDrive,
  Users,
  CheckCircle,
  AlertCircle,
  Clock,
  Filter,
} from "lucide-react";
import { Document } from "../types";
import { notifyError, notifySuccess } from "../utils/Toastify";
import Tooltip from "@mui/material/Tooltip";

const Documents: React.FC = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [uploadProgress, setUploadProgress] = useState<{
    [key: string]: number;
  }>({});
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterFileType, setFilterFileType] = useState<string>("all");
  const [sortBy, setSortBy] = useState<
    "created_at" | "file_size" | "original_filename"
  >("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const hasPermission = (user?.role_level || 1) <= 3;

  useEffect(() => {
    loadDocuments();

    // Auto-refresh for processing status
    // const interval = setInterval(loadDocuments, 10000);
    // return () => clearInterval(interval);
  }, []);

  const loadDocuments = async () => {
    try {
      const response = await api.get("/documents/");

      // Ensure we have an array, even if API returns something else
      const documentsData = Array.isArray(response.data)
        ? response.data
        : response.data?.documents || [];
      setDocuments(documentsData);
    } catch (error) {
      notifyError("Failed to load documents");
      // Set empty array on error to prevent filter issues
      setDocuments([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (files: FileList) => {
    const uploadPromises = Array.from(files).map(async (file) => {
      const uploadId = Date.now().toString() + Math.random().toString();
      setUploadProgress((prev) => ({ ...prev, [uploadId]: 0 }));

      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("access_level", "business");
        formData.append("enable_text_extraction", "true");

        await api.post("/documents/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setUploadProgress((prev) => ({
                ...prev,
                [uploadId]: percentCompleted,
              }));
            }
          },
        });

        setUploadProgress((prev) => {
          const newProgress = { ...prev };
          delete newProgress[uploadId];
          return newProgress;
        });
        notifySuccess(`${file.name} uploaded successfully`);
      } catch (error: any) {
        setUploadProgress((prev) => {
          const newProgress = { ...prev };
          delete newProgress[uploadId];
          return newProgress;
        });
        notifyError(
          `Failed to upload ${file.name}: ${
            error.response?.data?.error?.message || error.message
          }`
        );
      }
    });

    await Promise.all(uploadPromises);
    loadDocuments();
  };

  const handleDeleteDocument = async (documentId: string, filename: string) => {
    if (!window.confirm(`Are you sure you want to delete "${filename}"?`)) {
      return;
    }

    try {
      await api.delete(`/documents/${documentId}`);
      notifySuccess(`${filename} has been deleted`);
      loadDocuments();
    } catch (error: any) {
      notifyError(
        error.response?.data?.error?.message || "Failed to delete document"
      );
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case "failed":
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      case "processing":
        return <Clock className="w-5 h-5 text-purple-600 animate-spin" />;
      case "pending":
        return <Clock className="w-5 h-5 text-yellow-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "status-success";
      case "failed":
        return "status-error";
      case "processing":
        return "status-info";
      case "pending":
        return "status-warning";
      default:
        return "status-info";
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Ensure documents is always an array before filtering
  const safeDocuments = Array.isArray(documents) ? documents : [];

  const filteredAndSortedDocuments = safeDocuments
    .filter((doc) => {
      if (filterStatus !== "all" && doc.processing_status !== filterStatus)
        return false;
      if (filterFileType !== "all" && doc.file_type !== filterFileType)
        return false;
      return true;
    })
    .sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      if (sortBy === "created_at") {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  const fileTypes = Array.from(
    new Set(safeDocuments.map((doc) => doc.file_type))
  );
  const canUpload = (user?.role_level || 8) <= 6;

  return (
    <div className="min-h-screen bg-[#0f172a]">
      <Header
        title="Document Management"
        subtitle={`Manage your ${safeDocuments.length} documents`}
      />

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Action Bar */}
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-4">
              <h2 className="text-lg font-medium text-white">Documents</h2>
              <button
                onClick={loadDocuments}
                className="flex items-center space-x-1 text-sm text-purple-600 hover:text-purple-800"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Refresh</span>
              </button>
            </div>
            <Tooltip
              title={
                !hasPermission
                  ? "You do not have enough permissions for this action"
                  : "Upload document"
              }
              placement="top"
              arrow
            >
              <div className="flex items-center space-x-2 px-4 py-1 rounded-md">
                <label
                  className={`flex items-center space-x-2 px-3 py-1 rounded-md ${
                    hasPermission
                      ? "bg-[#8b3dff] cursor-pointer"
                      : "bg-gray-400 opacity-50 cursor-not-allowed pointer-events-none"
                  }`}
                >
                  <Upload className="w-4 h-4 text-white" />
                  <span className="text-white">Upload Files</span>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.csv,.txt,.docx,.md"
                    onChange={(e) =>
                      e.target.files && handleFileUpload(e.target.files)
                    }
                    className="hidden"
                  />
                </label>
              </div>
            </Tooltip>
          </div>

          {/* Upload Progress */}
          {Object.entries(uploadProgress).length > 0 && (
            <div className="mb-6  !bg-[#1e293b] !border-[#334155] border !rounded-lg">
              <div className="p-4">
                <h3 className="text-sm font-medium text-white mb-3">
                  Uploading Files...
                </h3>
                <div className="space-y-2">
                  {Object.entries(uploadProgress).map(([id, progress]) => (
                    <div key={id}>
                      <div className="flex justify-between text-xs text-gray-600 mb-1">
                        <span>Upload in progress...</span>
                        <span>{progress}%</span>
                      </div>
                      <div className="w-full bg-[#1e293b] rounded-full h-2">
                        <div
                          className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Filters and Sorting */}
          <div className="mb-6  !bg-[#1e293b] !border-[#334155] border !rounded-lg">
            <div className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <Filter className="w-4 h-4 text-[#64748b]" />
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="text-sm border border-[#334155] rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="all">All Status</option>
                      <option value="completed">Completed</option>
                      <option value="processing">Processing</option>
                      <option value="pending">Pending</option>
                      <option value="failed">Failed</option>
                    </select>
                  </div>

                  <select
                    value={filterFileType}
                    onChange={(e) => setFilterFileType(e.target.value)}
                    className="text-sm border border-[#334155] rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="all">All Types</option>
                    {fileTypes.map((type) => (
                      <option key={type} value={type}>
                        {type.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center space-x-2">
                  <span className="text-sm text-[#64748b]">Sort by:</span>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as any)}
                    className="text-sm border border-[#334155] rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="created_at">Date</option>
                    <option value="original_filename">Name</option>
                    <option value="file_size">Size</option>
                  </select>
                  <button
                    onClick={() =>
                      setSortOrder(sortOrder === "asc" ? "desc" : "asc")
                    }
                    className="text-sm text-purple-600 hover:text-purple-800"
                  >
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Documents List */}
          {isLoading ? (
            <div className="min-h-screen flex items-center justify-center bg-[#020617]">
              <LoadingSpinner size="lg" message="Loading documents..." />
            </div>
          ) : filteredAndSortedDocuments.length === 0 ? (
            <div className=" !bg-[#1e293b] !border-[#334155] border !rounded-lg">
              <div className="p-8 text-center">
                <div className="text-4xl text-gray-400 mb-4">📄</div>
                <p className="text-lg text-gray-600 mb-2">No documents found</p>
                <p className="text-sm text-[#64748b] mb-4">
                  {safeDocuments.length === 0
                    ? "Upload your first document to get started"
                    : "Try adjusting your filters"}
                </p>
                {canUpload && safeDocuments.length === 0 && (
                  <label className="btn-primary cursor-pointer inline-flex items-center space-x-2">
                    <Upload className="w-4 h-4" />
                    <span>Upload Document</span>
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.csv,.txt,.docx,.md"
                      onChange={(e) =>
                        e.target.files && handleFileUpload(e.target.files)
                      }
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            </div>
          ) : (
            <div className=" !bg-[#1e293b] !border-[#334155] border ">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-[#334155]">
                  <thead className="bg-[#1e293b] ">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#64748b] uppercase tracking-wider">
                        Document
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#64748b] uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#64748b] uppercase tracking-wider">
                        Size
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#64748b] uppercase tracking-wider">
                        Content
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#64748b] uppercase tracking-wider">
                        Uploaded
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-[#64748b] uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-bg-[#1e293b] divide-y divide-[#334155]">
                    {filteredAndSortedDocuments.map((doc) => (
                      <tr key={doc.id} className="hover:bg-[#1e293b]">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <FileText className="w-5 h-5 text-gray-400 mr-3" />
                            <div>
                              <div className="text-sm font-medium text-white">
                                {doc.original_filename}
                              </div>
                              <div className="text-xs text-[#64748b]">
                                {doc.file_type.toUpperCase()}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            {getStatusIcon(doc.processing_status)}
                            <span
                              className={`px-2 py-1 text-xs rounded ${getStatusColor(
                                doc.processing_status
                              )}`}
                            >
                              {doc.processing_status}
                            </span>
                          </div>
                          {doc.processing_error && (
                            <div className="text-xs text-red-600 mt-1">
                              {doc.processing_error}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-[#64748b]">
                          {formatFileSize(doc.file_size)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {doc.has_extracted_text ? (
                            <div className="text-sm">
                              <div className="text-green-600 font-medium">
                                {doc.extraction_word_count.toLocaleString()}{" "}
                                words
                              </div>
                              <div className="text-xs text-[#64748b]">
                                Extracted{" "}
                                {doc.extraction_date
                                  ? new Date(
                                      doc.extraction_date
                                    ).toLocaleDateString()
                                  : "recently"}
                              </div>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">
                              No text extracted
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-[#64748b]">
                          <div className="flex items-center space-x-1">
                            <Calendar className="w-4 h-4" />
                            <span>
                              {new Date(doc.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => {
                                // View document content
                                notifyError(
                                  "Document preview will be available in the next update"
                                );
                              }}
                              className="text-purple-600 hover:text-purple-800"
                              title="View document"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                // Download document
                                notifyError(
                                  "Document download will be available in the next update"
                                );
                              }}
                              className="text-green-600 hover:text-green-800"
                              title="Download document"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                            <Tooltip
                              title={
                                !hasPermission
                                  ? "You do not have enough permissions for this action"
                                  : "Delete document"
                              }
                              placement="top"
                              arrow
                            >
                              <button
                                onClick={() =>
                                  handleDeleteDocument(
                                    doc.id,
                                    doc.original_filename
                                  )
                                }
                                className={`${
                                  hasPermission
                                    ? "text-red-600 hover:text-red-800"
                                    : "text-gray-400 opacity-50 cursor-not-allowed pointer-events-none"
                                }`}
                                disabled={!hasPermission}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </Tooltip>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Document Statistics */}
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <div className=" !bg-[#1e293b] !border-[#334155] border !rounded-lg p-4">
              <div className="flex items-center">
                <FileText className="w-8 h-8 text-purple-600" />
                <div className="ml-3">
                  <div className="text-lg font-semibold text-white">
                    {safeDocuments.length}
                  </div>
                  <div className="text-sm text-[#64748b]">Total Documents</div>
                </div>
              </div>
            </div>

            <div className=" !bg-[#1e293b] !border-[#334155] border !rounded-lg p-4">
              <div className="flex items-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
                <div className="ml-3">
                  <div className="text-lg font-semibold text-white">
                    {
                      safeDocuments.filter(
                        (d) => d.processing_status === "completed"
                      ).length
                    }
                  </div>
                  <div className="text-sm text-[#64748b]">Processed</div>
                </div>
              </div>
            </div>

            <div className=" !bg-[#1e293b] !border-[#334155] border !rounded-lg p-4">
              <div className="flex items-center">
                <HardDrive className="w-8 h-8 text-purple-600" />
                <div className="ml-3">
                  <div className="text-lg font-semibold text-white">
                    {formatFileSize(
                      safeDocuments.reduce((sum, doc) => sum + doc.file_size, 0)
                    )}
                  </div>
                  <div className="text-sm text-[#64748b]">Total Size</div>
                </div>
              </div>
            </div>

            <div className=" !bg-[#1e293b] !border-[#334155] border !rounded-lg p-4">
              <div className="flex items-center">
                <FileType className="w-8 h-8 text-orange-600" />
                <div className="ml-3">
                  <div className="text-lg font-semibold text-white">
                    {fileTypes.length}
                  </div>
                  <div className="text-sm text-[#64748b]">File Types</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Documents;
